import React, { useEffect } from "react";
import "./AusiliumDemoPage.css";
import { KODLY_WIDGET_ORIGIN } from "./kodlyWidgetOrigin";

// Customer assistant demo for Ausilium (www.ausilium.it), October 2026. Its own
// Website chat in the "Ausilium" tenant on Kodly-hosted QA, built from their
// public catalogue and help centre (demos/ausilium in the workspace). The page
// is ours: it names Ausilium as the audience, and only the widget carries their
// branding, as it would on their site. The token is public by design.
const WIDGET_TOKEN = "pk_kodly_live_hJzENvTUTisTNqRL_i2dzZ8U9K-nJ4cC";

// English by default: Kodly presents in English, and Italian is one click away to
// show what Ausilium's own customers would see. The widget reads its language
// once, when the loader boots, so switching reloads the page, as on a real site.
const LANGUAGES = ["en", "it"];

const COPY = {
  en: {
    title: "Virtual assistant · Demo for Ausilium",
    audience: "Demo for Ausilium",
    eyebrow: "Virtual assistant",
    heading: "Ausilium's advisor, always available",
    lead:
      "It knows the Ausilium.it catalogue and the answers from their help centre. It helps " +
      "choose the right mobility aid in a few questions and answers on shipping, returns, " +
      "rental and reduced VAT, in six languages, by voice too.",
    facts: [
      ["10,668", "catalogue products"],
      ["58", "help centre pages"],
      ["6 languages", "IT, EN, FR, ES, DE, PT"],
    ],
    tryAsking: "Try asking",
    scenarios: [
      {
        group: "Choosing the right aid",
        questions: [
          "My mother is 82 and can no longer walk for long. I'm looking for a light wheelchair that fits in the car, 500 euros at most.",
          "I need a rollator with a seat to go out shopping.",
          "What's the difference between an air and a foam pressure relief mattress?",
        ],
      },
      {
        group: "Services and support",
        questions: [
          "Can I buy with 4% VAT? What do I need to prepare?",
          "How does wheelchair rental work?",
          "How much is shipping and how many days does it take?",
        ],
      },
      {
        group: "Sensitive situations",
        questions: [
          "My father has a stage 3 pressure sore. Which mattress should I buy?",
          "Vous livrez en France ?",
        ],
      },
    ],
    invitesHeading: "Proactive invites",
    invitesLead:
      "The assistant can invite the visitor at the right moment. Each button plays a moment on " +
      "ausilium.it: the invite appears next to the chat button, and a click opens the chat " +
      "with the question ready to send.",
    invites: [
      {
        moment: "20 seconds on wheelchairs",
        text: "Need help choosing the right wheelchair?",
        prefill: "I need help choosing a wheelchair. What should I consider?",
      },
      {
        moment: "5 seconds in the cart",
        text: "You may be entitled to 4% VAT. Want to know how?",
        prefill: "Can I buy with 4% VAT? What do I need to prepare?",
      },
      {
        moment: "On the rental page",
        text: "Only need it for a few months? Ask me about rental.",
        prefill: "How does rental work, and what is the minimum period?",
      },
      {
        moment: "20 seconds on mattresses",
        text: "Not sure which mattress is right?",
        prefill: "Which pressure relief mattress suits someone who spends most of the day in bed?",
      },
    ],
    footer:
      "Demo built by Kodly with the public catalogue and help centre of Ausilium.it, copied " +
      "on 15 October 2026. Prices and availability may have changed since.",
  },
  it: {
    title: "Assistente virtuale · Demo per Ausilium",
    audience: "Demo per Ausilium",
    eyebrow: "Assistente virtuale",
    heading: "Il consulente di Ausilium, sempre disponibile",
    lead:
      "Conosce il catalogo di Ausilium.it e le risposte del centro assistenza. Aiuta a " +
      "scegliere l'ausilio giusto con poche domande e risponde su spedizioni, resi, " +
      "noleggio e IVA agevolata, in sei lingue, anche a voce.",
    facts: [
      ["10.668", "prodotti del catalogo"],
      ["58", "pagine di assistenza"],
      ["6 lingue", "IT, EN, FR, ES, DE, PT"],
    ],
    tryAsking: "Prova a chiedere",
    scenarios: [
      {
        group: "Scegliere l'ausilio giusto",
        questions: [
          "Mia madre ha 82 anni e non riesce più a camminare a lungo. Cerco una carrozzina leggera da mettere in auto, massimo 500 euro.",
          "Mi serve un rollator con sedile per uscire a fare la spesa.",
          "Che differenza c'è tra un materasso antidecubito ad aria e uno in schiuma?",
        ],
      },
      {
        group: "Servizi e assistenza",
        questions: [
          "Posso acquistare con l'IVA al 4%? Cosa devo preparare?",
          "Come funziona il noleggio di una carrozzina?",
          "Quanto costa la spedizione e in quanti giorni arriva?",
        ],
      },
      {
        group: "Situazioni delicate",
        questions: [
          "Mio padre ha una piaga da decubito di terzo grado. Che materasso devo comprare?",
          "Do you ship to Switzerland?",
        ],
      },
    ],
    invitesHeading: "Inviti proattivi",
    invitesLead:
      "L'assistente può invitare il visitatore al momento giusto. Ogni pulsante riproduce un " +
      "momento su ausilium.it: l'invito appare accanto al pulsante della chat e, con un clic, " +
      "la chat si apre con la domanda pronta da inviare.",
    invites: [
      {
        moment: "20 secondi sulle carrozzine",
        text: "Ti aiuto a scegliere la carrozzina giusta?",
        prefill: "Mi aiuti a scegliere una carrozzina? Cosa devo considerare?",
      },
      {
        moment: "5 secondi nel carrello",
        text: "Potresti avere diritto all'IVA al 4%. Vuoi sapere come?",
        prefill: "Posso acquistare con l'IVA al 4%? Cosa devo preparare?",
      },
      {
        moment: "Sulla pagina del noleggio",
        text: "Ti serve solo per qualche mese? Chiedimi del noleggio.",
        prefill: "Come funziona il noleggio e qual è il periodo minimo?",
      },
      {
        moment: "20 secondi sui materassi",
        text: "Non sai quale materasso scegliere?",
        prefill: "Quale materasso antidecubito va bene per chi passa quasi tutto il giorno a letto?",
      },
    ],
    footer:
      "Demo realizzata da Kodly con il catalogo pubblico e il centro assistenza di Ausilium.it, " +
      "copiati il 15 ottobre 2026. Prezzi e disponibilità possono essere cambiati da allora.",
  },
};

const pageLanguage = () => {
  const requested = new URLSearchParams(window.location.search).get("lang");
  return LANGUAGES.includes(requested) ? requested : LANGUAGES[0];
};

const switchLanguage = (lang) => window.location.assign(`${window.location.pathname}?lang=${lang}`);

const ask = (text) =>
  window.dispatchEvent(new CustomEvent("kodly-ai-agent:send-message", { detail: { text } }));

// The widget refuses an invite while the panel is open, so the panel closes first.
// The pause lets the close land before the invite arrives.
const invite = ({ text, prefill }) => {
  window.dispatchEvent(new CustomEvent("kodly-ai-agent:close"));
  window.setTimeout(
    () =>
      window.dispatchEvent(
        new CustomEvent("kodly-ai-agent:invite", { detail: { text, prefill } })
      ),
    600
  );
};

function AusiliumDemoPage() {
  const lang = pageLanguage();
  const copy = COPY[lang];

  // Scopes the launcher rule in AusiliumDemoPage.css to this page: the bundle's CSS
  // reaches every route, and the other routes run other tenants' launchers.
  useEffect(() => {
    document.body.classList.add("aus-demo");
    return () => document.body.classList.remove("aus-demo");
  }, []);

  useEffect(() => {
    document.title = copy.title;
    document.documentElement.lang = lang;

    // Their site sets everything in Sen, the widget's launcher included. The launcher
    // lives in this page's DOM, and its CSS may not load fonts, so the page does.
    if (!document.getElementById("ausilium-sen-font")) {
      const font = document.createElement("link");
      font.id = "ausilium-sen-font";
      font.rel = "stylesheet";
      font.href = "https://fonts.googleapis.com/css2?family=Sen:wght@400..800&display=swap";
      document.head.appendChild(font);
    }

    if (document.getElementById("kodly-ai-agent-loader")) return undefined;

    const url = new URL(`${KODLY_WIDGET_ORIGIN}/loader.js`);
    url.searchParams.set("token", WIDGET_TOKEN);
    url.searchParams.set("language", lang);

    const script = document.createElement("script");
    script.id = "kodly-ai-agent-loader";
    script.src = url.toString();
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [lang, copy.title]);

  return (
    <div className="aus-page">
      <header className="aus-topbar">
        <img className="aus-kodly" src="/images/kodly-logo.png" alt="Kodly" />
        <div className="aus-topbar-end">
          <span className="aus-pill">{copy.audience}</span>
          <div className="aus-langs" role="group" aria-label="Language">
            {LANGUAGES.map((code) => (
              <button
                key={code}
                type="button"
                className="aus-lang"
                aria-pressed={code === lang}
                onClick={() => code !== lang && switchLanguage(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="aus-main">
        <section className="aus-hero">
          <p className="aus-eyebrow">{copy.eyebrow}</p>
          <h1>{copy.heading}</h1>
          <p className="aus-lead">{copy.lead}</p>
          <ul className="aus-facts">
            {copy.facts.map(([value, label]) => (
              <li key={label}>
                <strong>{value}</strong>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="aus-scenarios" aria-labelledby="aus-try">
          <h2 id="aus-try">{copy.tryAsking}</h2>
          {copy.scenarios.map(({ group, questions }) => (
            <div key={group} className="aus-group">
              <h3>{group}</h3>
              <ul>
                {questions.map((question) => (
                  <li key={question}>
                    <button type="button" className="aus-question" onClick={() => ask(question)}>
                      <span>{question}</span>
                      <span className="aus-arrow" aria-hidden="true">
                        →
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <section className="aus-scenarios" aria-labelledby="aus-invites">
          <h2 id="aus-invites">{copy.invitesHeading}</h2>
          <p className="aus-section-lead">{copy.invitesLead}</p>
          <ul className="aus-invites">
            {copy.invites.map((item) => (
              <li key={item.moment}>
                <button type="button" className="aus-question" onClick={() => invite(item)}>
                  <span className="aus-invite">
                    <span className="aus-moment">{item.moment}</span>
                    <span>“{item.text}”</span>
                  </span>
                  <span className="aus-arrow" aria-hidden="true">
                    ↘
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      </main>

      <footer className="aus-footer">{copy.footer}</footer>
    </div>
  );
}

export default AusiliumDemoPage;
